"use client"

import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import Link from "@/components/common/Link"
import Title from "@/components/common/Typography/Title"
import { useAppRouter } from "@/hooks/app/useAppRouter"
import { faFacebook, faInstagramSquare, faTiktok } from "@fortawesome/free-brands-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Col, ConfigProvider, Divider, Layout, Row, theme } from "antd"
import { TypeCategory, TypeStoreDetail } from '@/lib/redux/features/store/type'
import dynamic from "next/dynamic";
import { useParams, usePathname } from 'next/navigation'
import { gotoHomePage } from '@/utils/app/gotoHomePage'
import { getPageSizeProducts } from '@/utils/category/getPageSizeProducts'
import NextImage from '@/components/common/NextImage'
import Text from '@/components/common/Typography/Text'
import Carousel from '@/components/common/Carousel'
import { faChevronLeft, faChevronRight, faHome, faHomeAlt, faHomeLg } from '@fortawesome/free-solid-svg-icons'
import Flickity from 'flickity'
import { useIsDesktop } from '@/hooks/app/useIsDesktop'
import ButtonSideBar from './ButtonSideBar'
import { getSocailMediaItems } from '@/utils/store/getSocailMediaItems'
import { checkIfShownStoreName } from '@/utils/app/checkIfShownStoreName'
import { useShownQuickNavCategories } from '@/hooks/store/useShownQuickNavCategories'

const ButtonCart = dynamic(() => import("./ButtonCart"), { ssr: false, });

const BasicLayoutHeader = ({ store, categories }: { store: TypeStoreDetail, categories: TypeCategory[] }) => {
  const refCarousel = useRef<Flickity>(null)
  const router = useAppRouter()
  const params = useParams()
  const pathname = usePathname()
  const countShownProduct = getPageSizeProducts(store)
  const [shownNextPrev, setShownNextPrev] = useState<boolean>(false)
  const isDesktop = useIsDesktop()
  const isCrowdaa = store.platform_name === 'crowdaa'
  const isProductDetailPage = pathname.includes('/product/') && !!params.slug
  const isCategoryDetailPage = pathname.includes('/category/') && !!params.slug

  let name = 'Store'

  if (pathname.startsWith('/product/')) {
    name = 'Product'
  } else if (pathname.startsWith('/products/')) {
    name = 'Store'
  } else if (pathname.startsWith('/category/')) {
    name = 'Category'
  } else if (pathname.startsWith('/cart')) {
    name = 'Cart'
  } else if (pathname.startsWith('/checkout')) {
    name = 'Checkout'
  } else if (pathname.startsWith('/payment')) {
    name = 'Payment'
  }

  const isInHome = pathname === '/' || (pathname.startsWith('/products/') && params.page === '1')

  const items = getSocailMediaItems(store)

  const hiddenStoreName = !checkIfShownStoreName(store)
  const shownQuickNavBottom = useShownQuickNavCategories(store)

  const checkShownHideNextPrev = useCallback(() => {
    setShownNextPrev(!!refCarousel.current && refCarousel.current.slides.length > 1)
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  const categoriesQuickNav = !categories.length ? <></> : (
    <Row wrap={false} justify='start' style={{ lineHeight: 1, marginInline: '-0.5rem', paddingBottom: !!store.hidden_store_name ? 0 : '0.5rem' }}>
      <Col flex={"calc(100% - 7.5rem)"} style={{ paddingInline: '0.5rem', maxWidth: `calc(100% - ${shownQuickNavBottom ? 7 : 9}.5rem)` }}>
        <Carousel
          flickityRef={(ref) => {
            refCarousel.current = ref;
            setShownNextPrev(ref.slides.length > 1)
            ref.on('change', checkShownHideNextPrev)
          }}
          options={{
            groupCells: true,
            prevNextButtons: false,
            pageDots: false,
            adaptiveHeight: true,
            cellAlign: 'left',
            contain: true
          }}
          style={{
            justifyContent: 'center'
          }}
        >
          {categories.map(cate => {
            return (
              <div key={cate.id} style={{ paddingRight: '3rem', height: '2.515rem' }}>
                <Row align='middle' style={{ height: '100%' }}>
                  <Link
                    href={(
                      countShownProduct >= cate.count_product
                        ? `/category/${cate.slug}`
                        : `/category/${cate.slug}/products/page/1`
                    )}
                  >
                    {['secondary'].includes(isDesktop ? store.category_card || '' : store.category_card_mobile || '') 
                  && !!store.shown_categories 
                  ? (
                    <Title level={2} style={{ fontSize: '1.6rem', fontWeight: 'normal', margin: 0 }}>
                      {cate.category_name}
                    </Title>
                  ) : (
                    <Text>
                      {cate.category_name}
                    </Text>
                  )}
                    
                  </Link>
                </Row>
              </div>
            )
          })}
        </Carousel>
      </Col>
      {shownNextPrev && (
        <Col style={{ paddingInline: '0.5rem', boxShadow: '-2px 0 0px 0 rgba(0,0,0,.08)' }}>
          <Row wrap={false} align='middle'>
            <Col>
              <Button
                icon={<FontAwesomeIcon icon={faChevronLeft} />}
                type='text'
                size='small'
                style={{
                  height: '2.4rem',
                  width: '2.4rem'
                }}
                onClick={() => refCarousel.current?.previous()}
              />
            </Col>
            <Divider style={{ borderColor: store.nav_menu_items_color, marginBlock: '0.1rem' }} orientation='vertical' />
            <Col>
              <Button
                icon={<FontAwesomeIcon icon={faChevronRight} />}
                type='text'
                size='small'
                style={{
                  height: '2.4rem',
                  width: '2.4rem'
                }}
                onClick={() => {
                  console.log('refCarousel.current', refCarousel.current?.next)
                  refCarousel.current?.next()
                }}
              />
            </Col>
          </Row>
        </Col>)}
    </Row>
  )

  return (
    <ConfigProvider
      theme={{
        token: {
          colorText: store.nav_menu_items_color
        }
      }}
    >
      <Layout.Header
        className={`basic_layout--header${!shownQuickNavBottom ? ' basic_layout--hidden-store_name' : ''}`}
      >
        <Row align="middle" justify="center" style={{ height: '100%' }}>
          <Col className="page__section">
            <Row style={{ lineHeight: '5rem', height: '5rem' }} wrap={false} align="middle" justify='space-between'>
              <Col>
                <Row wrap={false} gutter={5} align="middle">
                  {isCrowdaa && (
                    <Col>
                      <Button
                        htmlType="button"
                        onClick={() => {
                          if (!isInHome) {
                            if (window.history.length <= 1) {
                              const countShownProducts = getPageSizeProducts(store)
                              if (countShownProducts >= (store.count_product || 0)) {
                                router.push('/')
                              } else {
                                router.push('/products/page/1')
                              }
                            } else {
                              router.back()
                            }
                          }
                        }}
                        className="basic_layout--header__btn"
                        type="text"
                        icon={<FontAwesomeIcon icon={faChevronLeft} />}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '2.5rem',
                          height: 'auto',
                          width: 'auto',
                          paddingInline: 0
                        }}
                        aria-label="Go to previous page"
                      >
                        {isCrowdaa && (
                          <>
                            {name}
                          </>
                        )}
                      </Button>
                    </Col>
                  )}
                  {/* {store.logo && !isCrowdaa && (
                    
                  )} */}
                  {!isCrowdaa && (
                    <Col style={{ lineHeight: 1, marginRight: '1rem' }}>
                      <Button
                        htmlType="button"
                        onClick={() => {
                          gotoHomePage(router, store)
                        }}
                        className="basic_layout--header__btn"
                        type="text"
                        icon={!store.logo && (
                          <FontAwesomeIcon icon={faHomeAlt} />
                        )}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '2.5rem',
                          height: 'auto',
                          paddingInline: 0,
                          width: 'auto'
                        }}
                        aria-label="Go to previous page"
                      >
                        <Row align='middle' style={{ marginInline: '-0.5rem' }}>
                          {!!store.logo && (
                            <Col style={{ paddingInline: '0.5rem' }}>
                              <Row align='middle'>
                                <NextImage
                                  hiddenSpin
                                  style={{ cursor: 'pointer', objectFit: 'cover', maxHeight: 30, width: 'auto' }}
                                  src={store.logo}
                                  height={60}
                                  width={60}
                                  alt="Logo store in the header"
                                />
                              </Row>
                            </Col>
                          )}
                          {!hiddenStoreName && (
                            <Col style={{ paddingInline: '0.5rem' }}>
                              <Title ellipsis level={isProductDetailPage || isCategoryDetailPage ? 2 : 1} style={{ margin: 0, fontSize: '2.4rem', maxWidth: '100%' }}>
                                {store.store_name}
                              </Title>
                            </Col>
                          )}
                        </Row>

                      </Button>

                    </Col>
                  )}
                </Row>
              </Col>
              <Col>
                <Row wrap={false} align='middle' style={{ marginInline: '-0.25rem', height: '5rem' }}>
                  {(isDesktop || (store.shown_categories !== true && isDesktop)) && (
                    <>
                      {items.map(({ icon, url, type }: any, idx) => {
                        if (!url || !icon) {
                          return <Fragment key={idx} />
                        }
                        return (
                          <Col key={idx} style={{ paddingInline: '0.25rem', height: '5rem' }}>
                            <Button
                              styles={{
                                icon: {
                                  alignItems: 'center',
                                  display: 'flex'
                                }
                              }}
                              onClick={() => window.open(url, '_blank')}
                              size="large"
                              type='text'
                              style={{ fontSize: '2.5rem', fontWeight: 'bold', height: "5rem", width: 'auto' }}
                              icon={typeof icon === 'string' ? <NextImage hiddenSpin src={icon} height={60} width={60} alt={`${type} icon`} style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} /> : <FontAwesomeIcon icon={icon} />}
                            />
                          </Col>
                        )
                      })}
                      {!!items.length && <Divider style={{ borderColor: store.nav_menu_items_color }} orientation='vertical' />}
                    </>
                  )}
                  <Col style={{ height: '5rem' }}>
                    <ButtonCart />
                  </Col>
                  {!isDesktop && store.shown_categories === true && (
                    <Col style={{ marginLeft: '1rem', height: '5rem' }}>
                      <ButtonSideBar store={store} categories={categories} />
                    </Col>
                  )}
                </Row>

              </Col>
            </Row>
            {shownQuickNavBottom && categoriesQuickNav}
          </Col>
        </Row>
      </Layout.Header>
    </ConfigProvider>
  )
}

export default BasicLayoutHeader